/**
 * Whether two versions of a room save as the same room: their optimized
 * forms (`getOptimizedFloorPlan`) hold the same values. An edit that only
 * writes a default, or puts back the value a field had, changes nothing that
 * saves.
 */
import { getOptimizedFloorPlan } from './getOptimizedFloorPlan';
import type { FloorPlanRoom } from './schemas';

/** The keys JSON would write: a key holding `undefined` is left out. */
const writtenKeys = (record: Record<string, unknown>): string[] => Object.keys(record).filter(key => record[key] !== undefined);

/** Equal JSON values: the same primitives, arrays of equal items in order, objects with equal values under the same written keys. */
const sameJsonValue = (value: unknown, other: unknown): boolean => {
  if (value === other) return true;
  if (typeof value !== 'object' || typeof other !== 'object' || value === null || other === null) return false;
  if (Array.isArray(value) !== Array.isArray(other)) return false;
  const record = value as Record<string, unknown>, otherRecord = other as Record<string, unknown>;
  const keys = writtenKeys(record);
  return keys.length === writtenKeys(otherRecord).length && keys.every(key => sameJsonValue(record[key], otherRecord[key]));
};

export const sameSavedRoom = (room: FloorPlanRoom, other: FloorPlanRoom): boolean =>
  room === other || sameJsonValue(getOptimizedFloorPlan(room), getOptimizedFloorPlan(other));
